import { startCase } from 'lodash';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { BenefitsEligibilityResultValue, SnfBucket } from './benefitsEligibility';

// TEMPORARY (OLIO-2301): a stopgap summary that stops the "Objects are not valid as a React
// child" crash and surfaces the key eligibility fields inline on the action card. The real,
// designed eligibility screens replace this next — treat this component as throwaway.
//
// Renders the integration-produced eligibility result (`benefits_eligibility_result`) as a
// readable summary. Reads ONLY from `summary` — every leaf is a string/number, never an
// object (the whole point: this is what replaced `{value}` in CompletedResult, which
// crashed when `value` became this nested object).

// "20240401" -> "2024-04-01"; passthrough for anything not YYYYMMDD.
function fmtYmd(s: string | null | undefined): string | null {
  if (!s) return null;
  return /^\d{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s;
}

const COVERAGE_TONE: Record<string, string> = {
  active: colors.accentGreen,
  inactive: colors.accentRed,
};

function coverageLabel(v?: string): string {
  return v ? startCase(v) : 'Unknown';
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  non_covered: 'Not covered',
  not_reported: 'Not reported',
};

function statusLabel(status?: string): string {
  if (!status) return 'Not reported';
  return STATUS_LABEL[status] ?? startCase(status);
}

// A per-network skilled-nursing bucket -> a single "Active · 50% coinsurance/visit · 150
// days/year · prior auth may be required" line. Only includes parts the payer reported.
function bucketParts(bucket: SnfBucket): string[] {
  const parts: string[] = [statusLabel(bucket.status)];
  const per = bucket.per ? `/${bucket.per.toLowerCase()}` : '';

  if (bucket.coinsurancePercent != null)
    parts.push(`${Math.round(bucket.coinsurancePercent * 100)}% coinsurance${per}`);
  if (bucket.copay != null) parts.push(`$${bucket.copay} copay${per}`);
  if (bucket.deductible != null) parts.push(`$${bucket.deductible} deductible`);
  if (bucket.outOfPocket != null) parts.push(`$${bucket.outOfPocket} out-of-pocket`);

  const dl = bucket.dayLimit;
  if (dl?.value != null) parts.push(`${dl.value} ${dl.unit ?? 'days'}${dl.period ? `/${dl.period}` : ''}`);

  const auth = bucket.priorAuth?.required;
  if (auth === 'yes') parts.push('prior auth required');
  else if (auth === 'maybe') parts.push('prior auth may be required');
  else if (auth === 'no') parts.push('no prior auth');

  return parts;
}

function hasSignal(bucket?: SnfBucket): bucket is SnfBucket {
  return !!bucket && !!bucket.status && bucket.status !== 'not_reported';
}

export default function BenefitsEligibilityResult({ value }: { value: BenefitsEligibilityResultValue }) {
  const summary = value.summary ?? {};
  const coverage = summary.activeCoverage;
  const asOf = fmtYmd(summary.coverageDates?.start);
  const snf = summary.skilledNursing;

  const networkRows: { label: string; text: string }[] = [];
  if (snf?.skilledNursingReported) {
    if (hasSignal(snf.inNetwork))
      networkRows.push({ label: 'In-network', text: bucketParts(snf.inNetwork).join(' · ') });
    if (hasSignal(snf.outNetwork))
      networkRows.push({ label: 'Out-of-network', text: bucketParts(snf.outNetwork).join(' · ') });
  }

  return (
    <Wrapper data-testid='benefits-eligibility-result'>
      <Line>
        <Key>Coverage</Key>
        <Val $color={coverage ? COVERAGE_TONE[coverage] : undefined}>
          {coverageLabel(coverage)}
          {asOf ? ` — in force since ${asOf}` : ''}
        </Val>
      </Line>

      <Line>
        <Key>Skilled nursing</Key>
        {snf?.skilledNursingReported ? (
          networkRows.length > 0 ? (
            <SnfRows>
              {networkRows.map((row) => (
                <SnfRow key={row.label}>
                  <SnfNet>{row.label}:</SnfNet> {row.text}
                </SnfRow>
              ))}
            </SnfRows>
          ) : (
            <Val>Reported</Val>
          )
        ) : (
          <Val $muted>Not reported{snf?.reason ? ` (${startCase(snf.reason)})` : ''}</Val>
        )}
      </Line>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Line = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Key = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const Val = styled(BodySmallBold)<{ $color?: string; $muted?: boolean }>`
  color: ${({ $color, $muted }) => $color ?? ($muted ? colors.black50 : colors.black)};
`;

const SnfRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SnfRow = styled(BodySmall)`
  color: ${colors.black75};
`;

const SnfNet = styled.span`
  font-weight: 700;
  color: ${colors.black};
`;
